import { useEffect, useRef, useState } from 'react';

import { apiGet } from '@/lib/http/client';

import { validateSlugLocal } from './slugConstants';

export type SlugStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'taken'
  | 'format-invalid'
  | 'reserved'
  | 'error';

interface CheckSlugResponse {
  available: boolean;
  reason?: 'format' | 'reserved' | 'taken';
}

const DEBOUNCE_MS = 300;

export function useSlugAvailability(slug: string): SlugStatus {
  const [status, setStatus] = useState<SlugStatus>('idle');
  const latestRequestId = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (slug.trim() === '') {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      setStatus('idle');
      return;
    }

    const { isValid, reason } = validateSlugLocal(slug);
    if (!isValid) {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      setStatus(reason === 'reserved' ? 'reserved' : 'format-invalid');
      return;
    }

    setStatus('checking');

    if (debounceTimer.current !== null) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      latestRequestId.current += 1;
      const requestId = latestRequestId.current;

      apiGet<CheckSlugResponse>(
        '/api/public/scheduling/booking-types/check-slug/?slug=' +
          encodeURIComponent(slug)
      )
        .then(response => {
          if (requestId !== latestRequestId.current) {
            return;
          }
          if (response.available) {
            setStatus('available');
          } else if (response.reason === 'taken') {
            setStatus('taken');
          } else if (response.reason === 'format') {
            setStatus('format-invalid');
          } else if (response.reason === 'reserved') {
            setStatus('reserved');
          } else {
            setStatus('taken');
          }
        })
        .catch(err => {
          if (requestId === latestRequestId.current) {
            console.warn('Slug availability check failed:', err);
            setStatus('error');
          }
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      latestRequestId.current += 1;
    };
  }, [slug]);

  return status;
}
